import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ScanSearch as Briefcase, Loader2, RefreshCw } from "lucide-react";
import { TabIntro } from "@/pages/candidates/TabIntro";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmptyState, SkeletonRows } from "@/components/app/Common";
import { api, get, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STAFF } from "@/lib/nav";
import { CareerGantt } from "@/pages/candidates/experience/CareerGantt";
import { RoleCards, GapsPanel, ChecklistPanel } from "@/pages/candidates/experience/ExperiencePanels";

function ForApplication({ appId }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const key = ["app-experience", appId];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => get(`/applications/${appId}/experience`),
    refetchInterval: (q) => (["queued", "processing"].includes(q.state.data?.status) ? 4000 : false) });
  if (isLoading) return <SkeletonRows rows={6} />;
  const run = async () => { try { await api.post(`/applications/${appId}/experience/run`); toast.success("Experience review queued"); qc.invalidateQueries({ queryKey: key }); } catch (e) { toast.error(errMsg(e)); } };
  const busy = ["queued", "processing"].includes(data.status);
  const e = data.experience;
  const runBtn = STAFF.includes(user.role) && <Button size="sm" variant="outline" disabled={busy} onClick={run} data-testid="experience-run-btn">{busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {e ? "Re-run review" : "Run experience review"}</Button>;
  if (!e) {
    return <EmptyState testId="experience-empty" icon={Briefcase} title={busy ? "Reviewing career history…" : "No experience review yet"}
      text={data.status === "failed" ? data.error : `Triggered when the candidate is moved to the Experience review stage for ${data.job_title}. You can also start it now.`} action={runBtn} />;
  }
  return (
    <div className="space-y-6" data-testid="experience-results">
      <div className="panel flex flex-wrap items-start gap-5 p-5">
        <div><p className="text-xs text-ink-muted">Experience Alignment</p><p className="font-display text-4xl font-semibold tabular-nums" data-tour="pt-experience-score" data-testid="experience-score">{e.score}</p></div>
        <div className="min-w-[240px] flex-1 space-y-2">
          <p className="text-sm leading-relaxed text-ink-secondary" data-testid="experience-rationale">{e.rationale}</p>
          <div className="flex flex-wrap gap-2 text-[11px] text-ink-muted" data-testid="experience-breakdown">
            {Object.entries(e.breakdown).map(([k, v]) => <span key={k} className="rounded-full border border-line px-2 py-0.5">{k.replace("_", " ")} {v}</span>)}
          </div>
        </div>
        {runBtn}
      </div>
      <CareerGantt roles={e.roles} gaps={e.gaps} />
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <RoleCards roles={e.roles} />
        <div className="space-y-6"><GapsPanel e={e} /><ChecklistPanel e={e} /></div>
      </div>
    </div>
  );
}

export function ExperienceTab({ c }) {
  const [appId, setAppId] = useState(c.applications[0]?.id);
  if (!c.applications.length) return <EmptyState icon={Briefcase} title="No applications" text="This candidate isn't linked to a job yet." />;
  return (
    <div className="space-y-4" data-testid="candidate-experience-tab">
      <TabIntro kind="experience" />
      {c.applications.length > 1 && (
        <select value={appId} onChange={(ev) => setAppId(ev.target.value)} className="h-9 rounded-lg border border-line bg-white px-2 text-sm" data-testid="experience-app-select">
          {c.applications.map((a) => <option key={a.id} value={a.id} label={a.job_title} />)}
        </select>
      )}
      <ForApplication key={appId} appId={appId} />
    </div>
  );
}
